import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { Modal, UnstyledLink } from '..'
import Button, { Sizes as ButtonSizes } from '../Button'
import Padding from '../Padding'
import { Flex, Body, HeaderGroup, ButtonsGroup } from './Confirmation.style'
import TextBox from './TextBox'

interface ConfirmationProps {
    cancel?: string
    children: JSX.Element
    className?: string
    confirmation: string | JSX.Element
    disabled?: boolean
    header?: string
    ok?: string
    onCancel?: () => void
    onChangeText?: (e: React.FormEvent) => void
    onConfirm?: () => void | boolean | null | Promise<void | boolean | null>
    onOpen?: () => void
    text?: string
    theme?: 'forestGreen' | 'danger'
    withTextArea?: boolean
}

function Confirmation(props: ConfirmationProps): React.ReactElement {
    const { t } = useTranslation()
    const {
        cancel = t('cancel'),
        children,
        className = '',
        confirmation,
        disabled = false,
        header = t('hold-on'),
        ok = t('ok'),
        onCancel = () => undefined,
        onChangeText = () => undefined,
        onConfirm = () => undefined,
        onOpen = () => undefined,
        text = '',
        theme = 'forestGreen',
        withTextArea = false,
        ...rest
    } = props

    const [isWorking, setIsWorking] = React.useState(false)
    const [showConfirmation, setShowConfirmation] = React.useState(false)

    const onClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
        if (typeof e.preventDefault === 'function') {
            e.preventDefault()
        }

        if (typeof e.stopPropagation === 'function') {
            e.stopPropagation()
        }

        if (disabled) {
            return
        }

        setShowConfirmation(true)

        if (onOpen) {
            onOpen()
        }
    }

    const onClickConfirm = async (e: React.MouseEvent<HTMLElement>): Promise<void> => {
        try {
            setIsWorking(true)

            if (typeof e.preventDefault === 'function') {
                e.preventDefault()
            }

            if (typeof e.stopPropagation === 'function') {
                e.stopPropagation()
            }

            const response = await onConfirm()

            if (typeof response === 'boolean' && !response) {
                return
            }

            setShowConfirmation(false)
        } catch (error) {
            console.error(error)
        } finally {
            setIsWorking(false)
        }
    }

    const onClickCancel = async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
        if (typeof e.preventDefault === 'function') {
            e.preventDefault()
        }

        if (typeof e.stopPropagation === 'function') {
            e.stopPropagation()
        }

        await onCancel()
        setShowConfirmation(false)
    }

    const renderBody = (): JSX.Element => {
        if (withTextArea) {
            return (
                <>
                    <Padding bottom>{confirmation}</Padding>
                    <TextBox onChange={onChangeText} value={text} />
                </>
            )
        }

        return <>{confirmation}</>
    }

    return (
        <>
            <UnstyledLink onClick={onClick} to="" className={className} {...rest}>
                {children}
            </UnstyledLink>
            <Modal isOpen={showConfirmation} onClose={onClickCancel}>
                <Flex>
                    <HeaderGroup>{header}</HeaderGroup>
                    <Body>{renderBody()}</Body>
                        <ButtonsGroup className="screenshot-buttons">
                            {props.onCancel && (
                                <Button theme="secondary" onClick={onClickCancel} size={ButtonSizes.sm}>
                                    {cancel}
                                </Button>
                            )}
                            <Button
                                theme={theme}
                                disabled={isWorking}
                                onClick={onClickConfirm}
                                size={ButtonSizes.sm}
                            >
                                {ok}
                            </Button>
                        </ButtonsGroup>
                </Flex>
            </Modal>
        </>
    )
}

export default Confirmation
